/**
 * Funciones para transformar datos del backend al formato del frontend
 */

interface Servicio {
  id: number;
  tipo: string;
  descripcion: string;
  precioVenta: number;
  precioCosto: number;
  moneda: string;
  monedaCosto: string;
  nroFile: string;
  revisado: boolean;
  revisadoCosto: boolean;
  proveedor?: string;
  proveedorId?: number;
  fechaViaje?: string;
}

interface Pago {
  id: number;
  observacion: string;
  moneda: string;
  importe: number;
  tipoCambio: number | null;
  montoConvertido: number | null;
  responsable_nombre: string;
  revisado_por_nombre: string;
  created_at?: string;
}

interface Pasajero {
  id: number;
  nombre: string;
  dni: string;
  pasaporte: string;
  fechaNacimiento: string;
}

/**
 * Transforma aéreos del backend al formato de servicios del frontend
 */
export const mapAereos = (aereosPaquetes: any[]): Servicio[] => {
  if (!Array.isArray(aereosPaquetes)) return [];
  return aereosPaquetes.map((aereo: any) => ({
    id: aereo.id,
    tipo: 'Aéreo',
    descripcion: aereo.descripcion || 'Servicio aéreo',
    precioVenta: parseFloat(aereo.importe) || 0,
    precioCosto: parseFloat(aereo.importe_costo) || 0,
    moneda: aereo.moneda || 'USD',
    monedaCosto: aereo.moneda_costo || 'USD',
    nroFile: aereo.nro_file || '',
    revisado: aereo.revisado || false,
    revisadoCosto: aereo.revisado_costo || false,
    proveedor: aereo.proveedor?.nombre || '',
    proveedorId: aereo.proveedor?.id,
    fechaViaje: aereo.fecha_viaje || undefined,
  }));
};

/**
 * Transforma hoteles del backend al formato de servicios del frontend
 */
export const mapHoteles = (hotelesPaquetes: any[]): Servicio[] => {
  if (!Array.isArray(hotelesPaquetes)) return [];
  return hotelesPaquetes.map((hotel: any) => ({
    id: hotel.id,
    tipo: 'Hotel',
    descripcion: hotel.descripcion || [hotel.hotel || hotel.nombre || 'Hotel', hotel.destino, hotel.noches ? `${hotel.noches} noches` : '', hotel.servicios_incluidos].filter(Boolean).join(' - '),
    precioVenta: parseFloat(hotel.importe) || 0,
    precioCosto: parseFloat(hotel.importe_costo) || 0,
    moneda: hotel.moneda || 'USD',
    monedaCosto: hotel.moneda_costo || 'USD',
    nroFile: hotel.nro_file || '',
    revisado: hotel.revisado || false,
    revisadoCosto: hotel.revisado_costo || false,
    proveedor: hotel.proveedor?.nombre || '',
    proveedorId: hotel.proveedor?.id,
    fechaViaje: hotel.fecha_viaje || undefined,
  }));
};

/**
 * Transforma servicios extra del backend al formato de servicios del frontend
 */
export const mapServicios = (serviciosPaquetes: any[]): Servicio[] => {
  if (!Array.isArray(serviciosPaquetes)) return [];
  return serviciosPaquetes.map((servicio: any) => ({
    id: servicio.id,
    tipo: 'Extra',
    descripcion: servicio.descripcion || 'Servicio adicional',
    precioVenta: parseFloat(servicio.importe) || 0,
    precioCosto: parseFloat(servicio.importe_costo) || 0,
    moneda: servicio.moneda || 'USD',
    monedaCosto: servicio.moneda_costo || 'USD',
    nroFile: servicio.nro_file || '',
    revisado: servicio.revisado || false,
    revisadoCosto: servicio.revisado_costo || false,
    proveedor: servicio.proveedor?.nombre || '',
    proveedorId: servicio.proveedor?.id,
    fechaViaje: servicio.fecha_viaje || undefined,
  }));
};

/**
 * Combina todos los servicios del paquete
 */
export const mapAllServicios = (paqueteCompleto: any): Servicio[] => {
  const fechaSalidaPaquete = paqueteCompleto?.fecha_salida || undefined;

  return [
    ...mapAereos(paqueteCompleto.aereosPaquetes || []),
    ...mapHoteles(paqueteCompleto.hotelesPaquetes || []),
    ...mapServicios(paqueteCompleto.serviciosPaquetes || []),
  ].map((s) => ({ ...s, fechaViaje: s.fechaViaje || fechaSalidaPaquete }));
};

/**
 * Transforma pagos del backend al formato del frontend
 */
export const mapPagos = (pagosPaquetes: any[], pagosLocal: any[] = []): Pago[] => {
  if (!Array.isArray(pagosPaquetes)) return pagosLocal;
  return pagosPaquetes
    .filter((pago: any) => !pago.deleted_at)
    .map((pago: any) => ({
      id: pago.id,
      observacion: pago.observacion || '-',
      moneda: pago.moneda || 'USD',
      importe: parseFloat(pago.importe || '0'),
      tipoCambio: pago.tipoCambio ?? pago.tipo_cambio ?? pago.tc ?? null,
      montoConvertido: pago.montoConvertido ?? pago.monto_convertido ?? pago.montoUSD ?? pago.monto_usd ?? null,
      responsable_nombre: pago.responsable_nombre ?? '',
      revisado_por_nombre: pago.revisado_por_nombre ?? '',
      created_at: pago.created_at ?? pago.created ?? null
    }));
};

/**
 * Transforma pasajeros del backend al formato del frontend
 */
export const mapPasajeros = (paqueteCompleto: any, pasajerosLocal: any[] = []): Pasajero[] => {
  const pasajerosPaquetes = paqueteCompleto.pasajerosPaquetes || paqueteCompleto.pasajeros;
  if (!Array.isArray(pasajerosPaquetes)) return pasajerosLocal;
  return pasajerosPaquetes
    .filter((pasajero: any) => !pasajero.deleted_at)
    .map((pasajero: any) => ({
      id: pasajero.id,
      nombre: pasajero.nombre || pasajero.apellido_nombre || '-',
      dni: pasajero.dni || pasajero.documento || '-',
      pasaporte: pasajero.pasaporte || '',
      fechaNacimiento: pasajero.fecha_nacimiento || ''
    }));
};

/**
 * Parsea observaciones del backend (puede venir como JSON string o array)
 */
export const parseObservaciones = (observaciones: any): any[] => {
  if (!observaciones) return [];
  try {
    if (typeof observaciones === 'string') {
      let parsed = JSON.parse(observaciones);
      if (typeof parsed === 'string') parsed = JSON.parse(parsed);
      return Array.isArray(parsed) ? parsed : [];
    } else if (Array.isArray(observaciones)) {
      return observaciones;
    }
  } catch (error) {
    if (typeof observaciones === 'string' && observaciones.trim().length > 0) {
      try {
        const cleanStr = observaciones.replace(/\n/g, '\\n').replace(/\r/g, '\\r');
        let parsed = JSON.parse(cleanStr);
        if (typeof parsed === 'string') parsed = JSON.parse(parsed);
        if (Array.isArray(parsed)) return parsed;
      } catch (e2) {
        // Falló totalmente el parseo JSON. Es información en texto plano migrada.
        return [{
          descripcion: observaciones.trim(),
          fecha: 'Migrada',
          usuario: 'SISTEMA_ANTERIOR'
        }];
      }
    }
    return [];
  }
  return [];
};

/**
 * Genera observaciones automáticas para servicios sin observaciones existentes
 */
export const generateAutoObservaciones = (
  paqueteCompleto: any,
  getCurrentUsername: () => string,
  todayISO: () => string
): any[] => {
  const usuarioObs = getCurrentUsername();
  const fechaObs = todayISO();
  const fromLines = (lines: string[]) => ({
    descripcion: lines.filter(Boolean).join('\n'),
    fecha: fechaObs,
    usuario: usuarioObs
  });

  const auto: any[] = [];

  // Aéreos
  (paqueteCompleto.aereosPaquetes || []).forEach((a: any) => {
    auto.push(fromLines([
      'Nuevo aéreo',
      `Aerolinea: ${a?.descripcion?.split(' - ')[0] || a?.aerolinea || ''}`,
      `Destino: ${(a?.descripcion?.split(' - ')[1] || a?.destino || '')}`,
      `Descripción: ${a?.descripcion || ''}`,
    ]));
    if (a?.importe_costo) {
      auto.push(fromLines([
        'Nuevo aéreo costo',
        `Aerolinea: ${a?.descripcion?.split(' - ')[0] || a?.aerolinea || ''}`,
        `Destino: ${(a?.descripcion?.split(' - ')[1] || a?.destino || '')}`,
        `Descripción: ${a?.descripcion || ''}`,
        `Costo: ${(a?.moneda_costo || 'USD')} ${a?.importe_costo}`,
      ]));
    }
  });

  // Hoteles
  (paqueteCompleto.hotelesPaquetes || []).forEach((h: any) => {
    auto.push(fromLines([
      'Nuevo servicio',
      `Descripción: ${(h?.descripcion || '').toString()}`,
    ]));
    if (h?.importe_costo) {
      auto.push(fromLines([
        'Nuevo servicio costo',
        `Descripción: ${(h?.descripcion || '').toString()}`,
        `Costo: ${(h?.moneda_costo || 'USD')} ${h?.importe_costo}`,
      ]));
    }
  });

  // Extras
  (paqueteCompleto.serviciosPaquetes || []).forEach((s: any) => {
    auto.push(fromLines([
      'Nuevo servicio',
      `Descripción: ${(s?.descripcion || '').toString()}`,
    ]));
    if (s?.importe_costo) {
      auto.push(fromLines([
        'Nuevo servicio costo',
        `Descripción: ${(s?.descripcion || '').toString()}`,
        `Costo: ${(s?.moneda_costo || 'USD')} ${s?.importe_costo}`,
      ]));
    }
  });

  return auto;
};
